import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Col, Collapse, Descriptions, Empty, List, Progress, Row, Space, Statistic, Tag, Timeline, Tooltip, Typography } from 'antd';
import { ArrowUpOutlined, CheckCircleOutlined, CrownOutlined, DollarOutlined, InfoCircleOutlined, ReloadOutlined, RocketOutlined, StarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import usePlan from '../../hooks/usePlan';
import PlanBadge from '../common/PlanBadge';
import UpgradeModal from './UpgradeModal';
import { PLANS, PLAN_FEATURES, planService } from '../../services/planService';
import { subscriptionApi } from '../../utils/api';
import { formatFeatureName, formatLimitPeriod, formatPlanLimit, formatPlanPrice, getPlanLimits, PLAN_LIMITS } from '../../utils/planPresentation';

const { Title, Text } = Typography;

/**
 * Présente l'abonnement, les consommations réellement mesurées et le détail
 * des offres accessibles. Les offres viennent de l'API centrale afin que les
 * nouveautés ajoutées aux plans apparaissent automatiquement dans le profil.
 */
const SubscriptionManagement = () => {
  const { planData, currentPlan, isTrialPlan, trialMessagesUsed, trialMaxMessages, loading, checkMessageLimit, refreshPlan } = usePlan();
  const [plansConfig, setPlansConfig] = useState(PLAN_FEATURES);
  // Consommation par quota, indexée par clé de quota (voir PLAN_LIMITS)
  const [usageStats, setUsageStats] = useState({});
  const [billingHistory, setBillingHistory] = useState([]);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Charge au montage la configuration d'offres maintenue côté serveur.
   */
  useEffect(() => {
    const loadPlansConfig = async () => {
      const availablePlans = await planService.getPlansConfig();
      setPlansConfig(availablePlans);
    };
    loadPlansConfig();
  }, []);

  /**
   * Actualise les compteurs et l'historique à chaque chargement ou changement
   * d'offre afin d'afficher les plafonds réellement associés à l'utilisateur.
   */
  useEffect(() => {
    if (!currentPlan) return;
    loadUsageStats();
    loadBillingHistory();
  }, [currentPlan, planData, plansConfig]);

  /**
   * Retrouve l'offre active, avec Découverte comme repli durant le chargement.
   */
  const currentPlanConfig = useMemo(() => {
    return plansConfig[currentPlan] || plansConfig[PLANS.DECOUVERTE] || {};
  }, [currentPlan, plansConfig]);

  /**
   * Identifie la prochaine offre dans l'ordre centralisé pour proposer une
   * évolution pertinente sans imposer de changement à l'utilisateur.
   */
  const nextPlanConfig = useMemo(() => {
    // Essai inversé : la suggestion est de conserver l'offre essayée
    if (isTrialPlan && currentPlan !== PLANS.DECOUVERTE) return plansConfig[currentPlan] || null;
    const order = planService.planOrder || Object.keys(plansConfig);
    const currentIndex = order.indexOf(currentPlan);
    return currentIndex >= 0 ? plansConfig[order[currentIndex + 1]] : null;
  }, [currentPlan, plansConfig, isTrialPlan]);

  /**
   * Quotas réellement appliqués : ceux enregistrés sur le compte (plan_features),
   * avec ceux du catalogue en repli. Pendant un essai inversé, le catalogue
   * affiche Essentiel alors que les quotas restent ceux de Découverte.
   */
  const effectiveLimits = useMemo(() => ({
    ...getPlanLimits(currentPlanConfig),
    ...(planData?.plan_features || {})
  }), [currentPlanConfig, planData]);

  // Offre réellement payée : aucune pendant un essai, toutes les offres
  // payantes restent donc proposées dans la modale (y compris l'offre essayée).
  const billedPlan = isTrialPlan ? PLANS.DECOUVERTE : currentPlan;

  /**
   * Récupère les compteurs connus du serveur et les associe aux quotas actifs.
   * Chaque quota de PLAN_LIMITS lit sa consommation via `usageKey` ; un quota
   * sans compteur (ex. utilisateurs) est affiché sans progression.
   */
  const loadUsageStats = async () => {
    try {
      const messageLimit = await checkMessageLimit();
      const response = await subscriptionApi.getUsageStats();
      const usageData = response?.data?.data || {};
      const limits = effectiveLimits;
      const stats = {};
      PLAN_LIMITS.forEach(({ key, usageKey }) => {
        stats[key] = {
          used: usageKey ? usageData[usageKey] ?? 0 : null,
          total: limits[key] ?? 0
        };
      });
      // Les messages gardent la source la plus précise (compteur d'essai ou usage mensuel)
      stats.max_messages_per_month = {
        used: usageData.messagesUsed || messageLimit?.used || trialMessagesUsed || 0,
        total: messageLimit?.total ?? (isTrialPlan ? trialMaxMessages : limits.max_messages_per_month ?? 0)
      };
      // Points achetés, affichés en complément de l'enveloppe mensuelle
      stats.ai_points_per_month.extra = usageData.aiPurchasedPoints || 0;
      setUsageStats(stats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  /**
   * Charge l'historique de facturation sans empêcher l'utilisation du profil
   * quand ce service est momentanément indisponible.
   */
  const loadBillingHistory = async () => {
    try {
      const response = await subscriptionApi.getBillingHistory();
      setBillingHistory(response.data?.success ? response.data.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement de l’historique:', error);
      setBillingHistory([]);
    }
  };

  /**
   * Met à jour explicitement l'offre, les plafonds et l'historique demandés.
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPlan();
      const availablePlans = await planService.getPlansConfig();
      setPlansConfig(availablePlans);
      await loadUsageStats();
      await loadBillingHistory();
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Retourne l'icône associée à chaque niveau d'offre.
   */
  const getPlanIcon = (plan) => ({
    [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
    [PLANS.STARTER]: <ThunderboltOutlined />,
    [PLANS.ESSENTIEL]: <RocketOutlined />,
    [PLANS.CROISSANCE]: <StarOutlined />,
    [PLANS.EXCELLENCE]: <CrownOutlined />
  }[plan] || <InfoCircleOutlined />);

  /**
   * Formate une date API au format français ou explicite son absence.
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseignée';
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  /**
   * Transforme une consommation en pourcentage borné pour la barre de progrès.
   */
  const getUsagePercentage = (used, total) => (!total || total === -1 ? 0 : Math.min(100, Math.round((used / total) * 100)));

  if (loading && !planData) return <div className="flex justify-center p-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>;

  const remainingTrialMessages = Math.max(0, trialMaxMessages - trialMessagesUsed);
  // Jours restants de connexion WhatsApp directe sur l'essai gratuit
  const trialEndDate = isTrialPlan && planData?.plan_end_date ? new Date(planData.plan_end_date) : null;
  const trialDaysLeft = trialEndDate ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / 86400000)) : null;

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <Space align="start" size="middle"><div className="text-3xl text-primary-600">{getPlanIcon(currentPlan)}</div><div><Title level={4} className="!mb-1">Mon abonnement</Title><PlanBadge showProgress={false} showUpgradeLink={false} /><div><Text type="secondary">{currentPlanConfig.description || 'Les informations de votre offre sont en cours de chargement.'}</Text></div></div></Space>
          {currentPlan !== PLANS.EXCELLENCE && <Button type="primary" icon={<ArrowUpOutlined />} onClick={() => setUpgradeModalVisible(true)}>Voir les offres</Button>}
        </div>
        <Descriptions className="mt-6" column={{ xs: 1, sm: 2, lg: 4 }} size="small">
          <Descriptions.Item label="Offre active">{currentPlanConfig.name || currentPlan}</Descriptions.Item>
          <Descriptions.Item label="Tarif">{isTrialPlan ? 'Essai gratuit' : formatPlanPrice(currentPlanConfig.price, currentPlanConfig.currency)}</Descriptions.Item>
          <Descriptions.Item label="Depuis">{formatDate(planData?.plan_start_date)}</Descriptions.Item>
          <Descriptions.Item label="Statut"><Tag color={isTrialPlan ? 'gold' : 'green'}>{isTrialPlan ? 'Essai en cours' : 'Actif'}</Tag></Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Essai gratuit : messages restants et jours de connexion WhatsApp directe restants */}
      {isTrialPlan && <Alert message="Période d’essai active" description={`${remainingTrialMessages} message${remainingTrialMessages > 1 ? 's' : ''} restant${remainingTrialMessages > 1 ? 's' : ''} sur ${trialMaxMessages}.${trialDaysLeft !== null ? ` Connexion WhatsApp directe : ${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} restant${trialDaysLeft > 1 ? 's' : ''} (les connexions par plateforme API ne sont pas limitées dans le temps).` : ''}`} type="warning" showIcon action={<Button size="small" type="primary" onClick={() => setUpgradeModalVisible(true)}>Comparer les offres</Button>} />}

      <Card title="Utilisation actuelle" extra={<Tooltip title="Rafraîchir les données"><Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>Actualiser</Button></Tooltip>}>
        {/* Une carte par quota de PLAN_LIMITS ; les quotas sans compteur n'affichent que leur plafond */}
        <Row gutter={[16, 16]}>{PLAN_LIMITS.map((limit) => {
          const usage = usageStats[limit.key] || { used: null, total: 0 };
          const hasCounter = usage.used !== null && usage.used !== undefined;
          // Quota sans enveloppe (ex. points IA sur l'essai) : on affiche le solde acheté ou offert
          const soldeSeul = usage.total === 0 && usage.extra > 0;
          const percentage = hasCounter ? getUsagePercentage(usage.used, usage.total) : 0;
          const suffix = usage.total === -1 ? '/ Illimité' : `/ ${usage.total}${formatLimitPeriod(limit, isTrialPlan)}`;
          return <Col xs={24} sm={12} lg={8} key={limit.key}><Card size="small">
            {soldeSeul
              ? <Statistic title={limit.label} value={usage.extra} suffix="points disponibles" />
              : <Statistic title={limit.label} value={hasCounter ? usage.used : formatPlanLimit(usage.total)} suffix={hasCounter ? suffix : formatLimitPeriod(limit, isTrialPlan)} />}
            {hasCounter && !soldeSeul && usage.total !== -1 && usage.total !== 0 && <Progress percent={percentage} status={percentage >= 90 ? 'exception' : 'normal'} className="mt-2" />}
            {!soldeSeul && usage.extra > 0 && <Text type="secondary" className="block mt-1">+ {usage.extra} points achetés disponibles</Text>}
          </Card></Col>;
        })}</Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}><Card title="Ce que comprend mon offre" className="h-full"><List size="small" dataSource={currentPlanConfig.features || []} locale={{ emptyText: 'Aucune fonctionnalité définie pour cette offre.' }} renderItem={(feature) => <List.Item><CheckCircleOutlined className="mr-2 text-green-500" />{formatFeatureName(feature)}</List.Item>} /></Card></Col>
        <Col xs={24} lg={12}><Card title="Tous mes quotas" className="h-full"><List size="small" dataSource={PLAN_LIMITS} renderItem={(limit) => <List.Item><Text>{limit.label}</Text><Text strong>{formatPlanLimit(effectiveLimits[limit.key])}{formatLimitPeriod(limit, isTrialPlan)}</Text></List.Item>} /></Card></Col>
      </Row>

      <Card title="Comparer les offres">
        <Collapse items={Object.entries(plansConfig).map(([planKey, plan]) => ({ key: planKey, label: <Space wrap><span>{getPlanIcon(planKey)}</span><Text strong>{plan.name}</Text><Tag>{formatPlanPrice(plan.price, plan.currency)}/mois</Tag>{planKey === currentPlan && <Tag color="green">Votre offre</Tag>}</Space>, children: <Row gutter={[16, 16]}><Col xs={24} md={12}><Text strong>Quotas</Text><List size="small" dataSource={PLAN_LIMITS} renderItem={(limit) => <List.Item><Text>{limit.label}</Text><Text>{formatPlanLimit(getPlanLimits(plan)[limit.key])}{formatLimitPeriod(limit, planKey === PLANS.DECOUVERTE)}</Text></List.Item>} /></Col><Col xs={24} md={12}><Text strong>Fonctionnalités incluses</Text><List size="small" dataSource={plan.features || []} renderItem={(feature) => <List.Item><CheckCircleOutlined className="mr-2 text-green-500" />{formatFeatureName(feature)}</List.Item>} /></Col></Row> }))} />
        <div className="mt-4"><Link to="/pricing"><Button type="link" className="p-0">Voir la comparaison complète des offres →</Button></Link></div>
      </Card>

      {nextPlanConfig && <Alert message={`Prochaine étape suggérée : ${nextPlanConfig.name}`} description={`${nextPlanConfig.recommended_for || 'Une offre plus adaptée à votre croissance.'} — ${formatPlanPrice(nextPlanConfig.price, nextPlanConfig.currency)}/mois.`} type="info" showIcon action={<Button onClick={() => setUpgradeModalVisible(true)}>Découvrir</Button>} />}

      <Card title="Historique de facturation">{billingHistory.length ? <Timeline items={billingHistory.map((item) => ({ dot: <DollarOutlined />, color: item.status === 'active' ? 'green' : 'gray', children: <div className="flex justify-between gap-4"><div><Text strong>{item.plan}</Text><div className="text-sm text-gray-500">{formatDate(item.date)}</div></div><div className="text-right"><div>{formatPlanPrice(item.amount, item.currency)}</div><Tag color={item.type === 'trial' ? 'gold' : 'green'}>{item.type === 'trial' ? 'Essai' : 'Payé'}</Tag></div></div> }))} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun historique de facturation disponible" />}</Card>

      <UpgradeModal visible={upgradeModalVisible} onCancel={() => setUpgradeModalVisible(false)} currentPlan={billedPlan} plansConfig={plansConfig} planOrder={planService.planOrder} />
    </div>
  );
};

export default SubscriptionManagement;
